<template>
  <div class="sa-role-picker-wrap" ref="wrapRef">
    <div
      v-if="readonly"
      class="sa-role-picker sa-role-picker--readonly"
      :class="rolePickerClass(modelValue)"
      :title="roleLabel(modelValue)"
      :aria-label="roleLabel(modelValue)"
    >
      <span class="sa-role-picker__badge" aria-hidden="true">
        <component :is="roleIcon(modelValue)" />
      </span>
      <span class="sa-role-picker__label">{{ roleLabel(modelValue) }}</span>
    </div>
    <button
      v-else
      ref="triggerRef"
      type="button"
      class="sa-role-picker"
      :class="[rolePickerClass(modelValue), { 'sa-role-picker--open': menuOpen }]"
      :aria-expanded="menuOpen"
      aria-haspopup="listbox"
      :aria-label="t('superAdmin.colRole')"
      @click.stop="toggleMenu"
    >
      <span class="sa-role-picker__badge" aria-hidden="true">
        <component :is="roleIcon(modelValue)" />
      </span>
      <span class="sa-role-picker__label">{{ roleLabel(modelValue) }}</span>
      <svg
        class="sa-role-picker__caret"
        :class="{ 'sa-role-picker__caret--open': menuOpen }"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
      </svg>
    </button>

    <Teleport v-if="!readonly" to="body">
      <div
        v-if="menuOpen"
        ref="menuRef"
        class="sa-role-menu"
        :style="menuStyle"
        role="presentation"
        @click.stop
      >
        <div class="sa-role-menu__head">{{ t('superAdmin.colRole') }}</div>
        <ul class="sa-role-menu__list" role="listbox" :aria-label="t('superAdmin.colRole')">
          <li v-for="opt in roleOptions" :key="opt.value" role="presentation">
            <button
              type="button"
              class="sa-role-menu__option"
              :class="{
                'sa-role-menu__option--active': modelValue === opt.value,
                [`sa-role-menu__option--${opt.class}`]: true
              }"
              role="option"
              :aria-selected="modelValue === opt.value"
              @click="selectRole(opt.value)"
            >
              <span class="sa-role-menu__option-icon" aria-hidden="true">
                <component :is="roleIcon(opt.value)" />
              </span>
              <span class="sa-role-menu__option-text">
                <strong>{{ t(opt.labelKey) }}</strong>
                <small>{{ t(opt.descKey) }}</small>
              </span>
              <svg
                v-if="modelValue === opt.value"
                class="sa-role-menu__check"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M5 13l4 4L19 7"/>
              </svg>
            </button>
          </li>
        </ul>
      </div>
    </Teleport>
  </div>
</template>

<script setup>
import { computed, h, nextTick, onBeforeUnmount, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

const props = defineProps({
  modelValue: { type: String, required: true },
  menuOpen: { type: Boolean, default: false },
  readonly: { type: Boolean, default: false }
})

const emit = defineEmits(['update:menuOpen', 'change'])

const { t } = useI18n()

const wrapRef = ref(null)
const triggerRef = ref(null)
const menuRef = ref(null)
const menuPos = ref({ top: 0, left: 0, minWidth: 220 })

const roleOptions = [
  { value: 'USER', labelKey: 'superAdmin.roleUser', descKey: 'superAdmin.createUserRoleUserDesc', class: 'user' },
  { value: 'ADMIN', labelKey: 'superAdmin.roleAdmin', descKey: 'superAdmin.createUserRoleAdminDesc', class: 'admin' },
  { value: 'SUPER_ADMIN', labelKey: 'superAdmin.roleSuperAdmin', descKey: 'superAdmin.createUserRoleSuperDesc', class: 'super' }
]

const menuStyle = computed(() => ({
  top: `${menuPos.value.top}px`,
  left: `${menuPos.value.left}px`,
  minWidth: `${menuPos.value.minWidth}px`
}))

function roleLabel (roleNome) {
  const opt = roleOptions.find((o) => o.value === roleNome)
  return opt ? t(opt.labelKey) : roleNome
}

function rolePickerClass (roleNome) {
  if (roleNome === 'SUPER_ADMIN') return 'sa-role-picker--super'
  if (roleNome === 'ADMIN') return 'sa-role-picker--admin'
  return 'sa-role-picker--user'
}

function roleIcon (roleNome) {
  if (roleNome === 'SUPER_ADMIN') {
    return () => h('svg', { fill: 'none', stroke: 'currentColor', viewBox: '0 0 24 24' }, [
      h('path', {
        'stroke-linecap': 'round',
        'stroke-linejoin': 'round',
        'stroke-width': '2',
        d: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z'
      })
    ])
  }
  if (roleNome === 'ADMIN') {
    return () => h('svg', { fill: 'none', stroke: 'currentColor', viewBox: '0 0 24 24' }, [
      h('path', {
        'stroke-linecap': 'round',
        'stroke-linejoin': 'round',
        'stroke-width': '2',
        d: 'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z'
      }),
      h('path', {
        'stroke-linecap': 'round',
        'stroke-linejoin': 'round',
        'stroke-width': '2',
        d: 'M15 12a3 3 0 11-6 0 3 3 0 016 0z'
      })
    ])
  }
  return () => h('svg', { fill: 'none', stroke: 'currentColor', viewBox: '0 0 24 24' }, [
    h('path', {
      'stroke-linecap': 'round',
      'stroke-linejoin': 'round',
      'stroke-width': '2',
      d: 'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z'
    })
  ])
}

function updateMenuPosition () {
  const el = triggerRef.value
  if (!el) return
  const rect = el.getBoundingClientRect()
  const menuWidth = 248
  const menuHeight = 220
  const gap = 6
  let left = rect.right - menuWidth
  left = Math.max(8, Math.min(left, window.innerWidth - menuWidth - 8))
  let top = rect.bottom + gap
  if (top + menuHeight > window.innerHeight - 8) {
    top = Math.max(8, rect.top - menuHeight - gap)
  }
  menuPos.value = {
    top,
    left,
    minWidth: Math.max(rect.width, menuWidth)
  }
}

function toggleMenu () {
  emit('update:menuOpen', !props.menuOpen)
}

function selectRole (value) {
  if (value !== props.modelValue) emit('change', value)
  emit('update:menuOpen', false)
}

function onClickOutside (event) {
  if (!props.menuOpen) return
  const inTrigger = triggerRef.value?.contains(event.target)
  const inMenu = menuRef.value?.contains(event.target)
  if (!inTrigger && !inMenu) emit('update:menuOpen', false)
}

function onEscape (event) {
  if (event.key === 'Escape' && props.menuOpen) {
    event.stopPropagation()
    emit('update:menuOpen', false)
  }
}

function onViewportChange () {
  if (props.menuOpen) updateMenuPosition()
}

function bindMenuListeners () {
  document.addEventListener('click', onClickOutside)
  document.addEventListener('keydown', onEscape)
  window.addEventListener('resize', onViewportChange)
  window.addEventListener('scroll', onViewportChange, true)
}

function unbindMenuListeners () {
  document.removeEventListener('click', onClickOutside)
  document.removeEventListener('keydown', onEscape)
  window.removeEventListener('resize', onViewportChange)
  window.removeEventListener('scroll', onViewportChange, true)
}

watch(() => props.menuOpen, async (open) => {
  if (open) {
    await nextTick()
    updateMenuPosition()
    bindMenuListeners()
  } else {
    unbindMenuListeners()
  }
})

onBeforeUnmount(unbindMenuListeners)
</script>

<style scoped>
.sa-role-picker-wrap {
  position: relative;
  min-width: 0;
}

.sa-role-picker {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  width: 100%;
  height: 32px;
  padding: 0 8px 0 4px;
  border-radius: 8px;
  border: 1px solid transparent;
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease, box-shadow 0.15s ease;
}

.sa-role-picker:hover,
.sa-role-picker--open {
  box-shadow: 0 1px 4px color-mix(in srgb, #000 6%, transparent);
}

.sa-role-picker--open .sa-role-picker__caret {
  transform: rotate(180deg);
}

.sa-role-picker__badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  border-radius: 6px;
}

.sa-role-picker__badge :deep(svg) {
  width: 14px;
  height: 14px;
}

.sa-role-picker__label {
  flex: 1;
  min-width: 0;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.01em;
  color: var(--text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: left;
}

.sa-role-picker__caret {
  width: 12px;
  height: 12px;
  flex-shrink: 0;
  color: var(--text-tertiary, var(--text-secondary));
  opacity: 0.75;
  transition: transform 0.15s ease;
}

.sa-role-picker--user {
  background: color-mix(in srgb, #64748b 10%, var(--bg-primary));
  border-color: color-mix(in srgb, #64748b 22%, var(--border-primary));
}

.sa-role-picker--user .sa-role-picker__badge {
  color: #475569;
  background: color-mix(in srgb, #64748b 14%, var(--bg-secondary));
}

.sa-role-picker--admin {
  background: color-mix(in srgb, #6366f1 10%, var(--bg-primary));
  border-color: color-mix(in srgb, #6366f1 28%, var(--border-primary));
}

.sa-role-picker--admin .sa-role-picker__badge {
  color: #4f46e5;
  background: color-mix(in srgb, #6366f1 16%, var(--bg-secondary));
}

.sa-role-picker--super {
  background: linear-gradient(
    135deg,
    color-mix(in srgb, #f59e0b 14%, var(--bg-primary)) 0%,
    color-mix(in srgb, #8b5cf6 12%, var(--bg-primary)) 100%
  );
  border-color: color-mix(in srgb, #f59e0b 32%, var(--border-primary));
}

.sa-role-picker--super .sa-role-picker__badge {
  color: #b45309;
  background: color-mix(in srgb, #f59e0b 20%, var(--bg-secondary));
}

.sa-role-picker--super .sa-role-picker__label {
  background: linear-gradient(90deg, #b45309, #7c3aed);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

.sa-role-picker--readonly {
  cursor: default;
  padding-right: 8px;
}
</style>

<style>
.sa-role-menu {
  position: fixed;
  z-index: 10070;
  padding: 6px;
  border-radius: 12px;
  border: 1px solid var(--border-primary);
  background: var(--bg-card);
  box-shadow:
    0 12px 32px color-mix(in srgb, #000 18%, transparent),
    0 2px 8px color-mix(in srgb, #000 8%, transparent);
  animation: sa-role-menu-in 0.14s ease-out;
}

@keyframes sa-role-menu-in {
  from {
    opacity: 0;
    transform: translateY(-4px) scale(0.98);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.sa-role-menu__head {
  padding: 6px 10px 8px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--text-tertiary, var(--text-secondary));
}

.sa-role-menu__list {
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.sa-role-menu__option {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 10px 10px;
  border: 1px solid transparent;
  border-radius: 10px;
  background: transparent;
  text-align: left;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease;
}

.sa-role-menu__option:hover {
  background: var(--bg-hover, color-mix(in srgb, var(--text-primary) 4%, var(--bg-card)));
}

.sa-role-menu__option--active {
  border-color: color-mix(in srgb, #6366f1 28%, transparent);
  background: color-mix(in srgb, #6366f1 8%, var(--bg-card));
  box-shadow: inset 0 0 0 1px color-mix(in srgb, #6366f1 12%, transparent);
}

.sa-role-menu__option-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 34px;
  height: 34px;
  border-radius: 8px;
}

.sa-role-menu__option-icon svg {
  width: 17px;
  height: 17px;
}

.sa-role-menu__option--user .sa-role-menu__option-icon {
  color: #475569;
  background: color-mix(in srgb, #64748b 14%, var(--bg-secondary));
}

.sa-role-menu__option--admin .sa-role-menu__option-icon {
  color: #4f46e5;
  background: color-mix(in srgb, #6366f1 14%, var(--bg-secondary));
}

.sa-role-menu__option--super .sa-role-menu__option-icon {
  color: #b45309;
  background: color-mix(in srgb, #f59e0b 18%, var(--bg-secondary));
}

.sa-role-menu__option-text {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.sa-role-menu__option-text strong {
  font-size: 13px;
  font-weight: 700;
  color: var(--text-primary);
  line-height: 1.25;
}

.sa-role-menu__option-text small {
  font-size: 11px;
  line-height: 1.35;
  color: var(--text-secondary);
}

.sa-role-menu__check {
  width: 18px;
  height: 18px;
  flex-shrink: 0;
  color: #4f46e5;
}
</style>
